import { useState, type ReactNode } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  MenuItem,
  TextField,
} from '@mui/material';
import { IconArrowRight, IconLayoutGrid, IconList, IconPhone } from '@tabler/icons-react';
import {
  useCampaignAgentListControllerListQuery,
  useCampaignControllerCampaignQuery,
  type CampaignListItemDto,
} from '@shared/services/apiService';
import { formatCampaignStatusLabel } from '@pages/Admin/Main/Campaign/ui/CampaignStatusButton/utils';
import { useDesignVariant } from './DesignVariantProvider';

const CampaignAssistant = ({ campaign }: { campaign: CampaignListItemDto }) => {
  const detail = useCampaignControllerCampaignQuery({ id: campaign.id });
  const agents = useCampaignAgentListControllerListQuery({ id: campaign.id });
  if (detail.isError || agents.isError)
    return (
      <Typography variant="caption" color="error">
        Unable to load assistants
      </Typography>
    );
  const rows = detail.currentData?.squad?.agents ?? agents.currentData;
  const agent = rows?.[0];
  if (!rows)
    return (
      <Typography variant="caption" color="text.secondary">
        Loading assistants…
      </Typography>
    );
  return agent ? (
    <Stack direction="row" gap={1.5} alignItems="center">
      <Avatar
        variant="rounded"
        src={agent.avatarUrl || undefined}
        sx={{
          bgcolor: 'primary.light',
          color: 'primary.main',
          width: 36,
          height: 36,
          fontSize: 13,
        }}>
        {agent.name.slice(0, 1)}
      </Avatar>
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {agent.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {agent.profile?.name || agent.voice?.name || 'Campaign instructions'}
          {rows.length > 1 ? ` · +${rows.length - 1} more` : ''}
        </Typography>
      </Box>
    </Stack>
  ) : (
    <Typography variant="caption" color="text.secondary">
      No assistant configured
    </Typography>
  );
};

/** Layout only. Every mutation and its guard still belongs to CampaignList. */
export const VariantCampaignList = <T extends CampaignListItemDto>({
  campaigns,
  onOpen,
  renderControls,
  renderActions,
  sortBy,
  sortOrder,
  onSort,
  onOrder,
  searchControl,
}: {
  searchControl?: ReactNode;
  campaigns: T[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (value: 'title' | 'createdAt') => void;
  onOrder: (value: 'asc' | 'desc') => void;
  onOpen: (id: string) => void;
  renderControls: (campaign: T) => ReactNode;
  renderActions: (campaign: T) => ReactNode;
}) => {
  const { variant } = useDesignVariant();
  const [views, setViews] = useState<Record<string, 'list' | 'cards'>>({});
  const view = views[variant] ?? (['v4', 'v5'].includes(variant) ? 'cards' : 'list');
  const sorting = (
    <Stack direction="row" gap={2} flexWrap="wrap" sx={{ p: 2 }}>
      {searchControl}
      <TextField
        select
        size="small"
        label="Sort team by"
        value={sortBy}
        onChange={(e) => onSort(e.target.value as 'title' | 'createdAt')}
        sx={{ minWidth: 150 }}>
        <MenuItem value="createdAt">Created date</MenuItem>
        <MenuItem value="title">Name</MenuItem>
      </TextField>
      <TextField
        select
        size="small"
        label="Order"
        value={sortOrder}
        onChange={(e) => onOrder(e.target.value as 'asc' | 'desc')}
        sx={{ minWidth: 130 }}>
        <MenuItem value="desc">Descending</MenuItem>
        <MenuItem value="asc">Ascending</MenuItem>
      </TextField>
    </Stack>
  );

  if (!campaigns.length)
    return (
      <>
        {sorting}
        <Stack alignItems="center" gap={2} sx={{ py: 8 }}>
          <IconPhone size={30} stroke={1.5} />
          <Typography variant="h6">Your next conversation starts here</Typography>
          <Typography variant="body2" color="text.secondary">
            Create a campaign or try another search.
          </Typography>
        </Stack>
      </>
    );
  return (
    <>
      {sorting}
      {['v2', 'v4', 'v5'].includes(variant) && (
        <Stack direction="row" justifyContent="end" sx={{ px: 2, pb: 2 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, next) => next && setViews((current) => ({ ...current, [variant]: next }))}
            aria-label="Team layout">
            <ToggleButton value="cards" aria-label="Team cards">
              <IconLayoutGrid size={17} />
            </ToggleButton>
            <ToggleButton value="list" aria-label="Team list">
              <IconList size={17} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}
      {variant === 'v1' ? (
        <Box>
          {campaigns.map((campaign) => (
            <Stack
              key={campaign.id}
              data-testid={`campaign-row-${campaign.id}`}
              direction="row"
              gap={2}
              alignItems="center"
              sx={{
                px: 3,
                py: 3.5,
                borderTop: 1,
                borderColor: 'divider',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
              }}>
              <Avatar
                variant="rounded"
                sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 42, height: 42 }}>
                <IconPhone size={22} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Button
                  onClick={() => onOpen(campaign.id)}
                  sx={{ p: 0, textAlign: 'start', color: 'text.primary', fontWeight: 650 }}>
                  {campaign.title}
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}>
                  {campaign.type === 'inbound' ? 'Inbound' : 'Outbound'} · {campaign.agentsCount}{' '}
                  {campaign.agentsCount === 1 ? 'agent' : 'agents'} · Created{' '}
                  {new Date(campaign.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
              <Chip
                data-testid={`campaign-status-${campaign.id}`}
                label={formatCampaignStatusLabel(campaign.status)}
                size="small"
                variant="outlined"
              />
              {renderControls(campaign)}
              <Button onClick={() => onOpen(campaign.id)} endIcon={<IconArrowRight size={16} />}>
                Open setup
              </Button>
              {renderActions(campaign)}
            </Stack>
          ))}
        </Box>
      ) : view === 'cards' && ['v2', 'v4', 'v5'].includes(variant) ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
            p: 2,
          }}>
          {campaigns.map((campaign) => (
            <Paper
              key={campaign.id}
              variant="outlined"
              data-testid={`campaign-row-${campaign.id}`}
              sx={{ p: 2.5 }}>
              <Stack gap={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                    <IconPhone size={21} />
                  </Avatar>
                  {renderActions(campaign)}
                </Stack>
                <Typography variant="h6">{campaign.title}</Typography>
                <CampaignAssistant campaign={campaign} />
                <Typography variant="body2" color="text.secondary">
                  {campaign.agentsCount} {campaign.agentsCount === 1 ? 'agent' : 'agents'} ·{' '}
                  {campaign.type === 'inbound' ? 'Incoming calls' : 'Outgoing calls'}
                </Typography>
                <Chip
                  label={formatCampaignStatusLabel(campaign.status)}
                  size="small"
                  variant="outlined"
                  sx={{ alignSelf: 'start' }}
                />
                <Stack direction="row" justifyContent="space-between">
                  {renderControls(campaign)}
                  <Button
                    onClick={() => onOpen(campaign.id)}
                    endIcon={<IconArrowRight size={16} />}>
                    Open setup
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer>
          <Table aria-label={variant === 'v2' ? 'My team' : 'Campaigns'} sx={{ minWidth: 740 }}>
            <TableHead>
              <TableRow>
                {[
                  'Campaign',
                  variant === 'v2' ? 'Assistant' : 'Agents',
                  'Status',
                  'Customers remaining / total',
                  'Actions',
                ].map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id} hover data-testid={`campaign-row-${campaign.id}`}>
                  <TableCell>
                    <Button
                      onClick={() => onOpen(campaign.id)}
                      sx={{ p: 0, color: 'text.primary', fontWeight: 650, textAlign: 'start' }}>
                      {campaign.title}
                    </Button>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5 }}>
                      {campaign.type === 'inbound' ? 'Incoming' : 'Outgoing'} · Created{' '}
                      {new Date(campaign.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {variant === 'v2' ? (
                      <CampaignAssistant campaign={campaign} />
                    ) : (
                      campaign.agentsCount
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      data-testid={`campaign-status-${campaign.id}`}
                      label={formatCampaignStatusLabel(campaign.status)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell data-testid={`campaign-customers-${campaign.id}`}>
                    {campaign.type === 'inbound'
                      ? 'N/A'
                      : `${campaign.customersLeft}/${campaign.customersTotal}`}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" gap={1} alignItems="center">
                      {renderControls(campaign)}
                      <Button
                        aria-label={`Open ${campaign.title}`}
                        onClick={() => onOpen(campaign.id)}>
                        <IconArrowRight size={17} />
                      </Button>
                      {renderActions(campaign)}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
